/**
 * TEXTO PARA BUSCAR — SIN TILDES Y SIN ESPACIOS DE SOBRA.
 *
 * POR QUÉ EXISTE. Los filtros que corren en el navegador comparaban así:
 *
 *   String(texto || "").toLowerCase()
 *     .includes(busqueda.toLowerCase())   ← el patrón que había
 *
 * Eso no quita tildes ni recorta espacios, y en un sistema salvadoreño ese
 * descuido es grave: Martínez, Hernández, Rodríguez, González y Ramírez son
 * los apellidos más comunes y todos llevan tilde. Buscar «Martínez» NO
 * encontraba a «Martinez», buscar «Martinez» NO encontraba a «Martínez», y
 * «␣␣juan␣␣» —con los espacios que deja el teclado del teléfono— no
 * encontraba nada. Un guardia que escribe bien el apellido se quedaba sin la
 * persona que tenía delante.
 *
 * REPLICA LA NORMALIZACIÓN DEL BACKEND A PROPÓSITO. `normalizar()` hace lo
 * mismo que `normalizarTexto()` en `backend/utils/cifradoCampos.js`, que es
 * la función con la que se construyen los índices ciegos del cifrado. Si los
 * dos lados normalizaran distinto, el servidor y el navegador encontrarían
 * cosas distintas con el mismo término y nadie sabría cuál de los dos miente.
 *
 * Y NO SE VUELVE A ESCRIBIR EN LÍNEA. El defecto existía precisamente porque
 * el mismo idioma estaba copiado quince veces: cualquier filtro nuevo llama
 * aquí.
 */

/**
 * Deja un texto en su forma canónica: sin tildes, en minúsculas y sin
 * espacios al inicio ni al final.
 *
 * `NFD` separa la letra de su acento, y el rango U+0300–U+036F —los signos
 * diacríticos combinables— se descarta: «í» queda en «i».
 *
 * Tolera `null`, `undefined` y números, porque los filtros reciben campos
 * opcionales. Es la única diferencia con el backend, que recibe el valor ya
 * presente: aquí un `null` tiene que dar cadena vacía y no la palabra
 * «null», que encontraría filas por un dato que no existe.
 */
export const normalizar = (valor) =>
  String(valor ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();

/**
 * ¿`texto` contiene a `termino`, ya normalizados los dos?
 *
 * Con el término vacío devuelve `true`: la caja de búsqueda vacía no filtra,
 * y con ella se siguen viendo todas las filas.
 */
export const coincide = (texto, termino) => {
  const aguja = normalizar(termino);
  if (aguja === "") return true;
  return normalizar(texto).includes(aguja);
};

/**
 * LA PLACA, EN UNA SOLA FORMA — el mismo criterio de
 * `backend/utils/placas.js`: se quita TODO lo que no sea letra o número, el
 * guión incluido, y se pasa a mayúsculas.
 *
 *   «P123-456», «p 123 456» y «P123456»  →  P123456
 *
 * Las placas se guardan ya normalizadas, así que comparar tal cual lo que se
 * teclea deja al guardia sin el carro por un guión.
 */
export const normalizarPlaca = (valor) =>
  String(valor ?? "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");

/**
 * ¿`placa` contiene a `termino`, comparadas las dos como placas?
 *
 * Mismo contrato que `coincide()`: un término que queda vacío no filtra.
 * Existe para que los dos sitios que comparan placas lo hagan igual.
 */
export const coincidePlaca = (placa, termino) => {
  const aguja = normalizarPlaca(termino);
  if (aguja === "") return true;
  return normalizarPlaca(placa).includes(aguja);
};

/**
 * «1 visita», no «1 visitas».
 *
 * Suena a detalle y no lo es: es el tipo de descuido que, en una pantalla que
 * una junta directiva mira para decidir algo, resta confianza a la cifra que
 * está al lado. Aparecía en cuatro sitios, cada uno con el plural pegado a
 * mano, así que vive aquí para que no haya un quinto.
 *
 * El femenino se forma solo cuando el singular termina en «a»; para las
 * palabras irregulares se pasa el plural explícito.
 */
export const plural = (n, singular, pluralExplicito = null) =>
  Number(n) === 1 ? singular : pluralExplicito || `${singular}s`;
